import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { ContentFitnessDimension } from "contracts/report/content-fitness/dimensions";
import {
  contentFitnessCanonicalCountForDimension,
  contentFitnessPilotSubcriteriaForDimension,
} from "contracts/report/content-fitness/lookups";
import { CONTENT_FITNESS_SUBCRITERIA } from "contracts/report/content-fitness/subcriteria";
import { createMemo } from "solid-js";
import type { ContentFitnessOrigin } from "../../enums/scan";
import { CONTENT_FITNESS_ORIGIN } from "../../enums/scan";
import { isOpen } from "../website/issueModel";
import {
  assessmentRowsForDimension,
  type DimensionAssessment,
  issueBelongsToDimension,
  uniquePages,
} from "./contentFitnessDimensionModel";
import { resolveIssuePages } from "./issuePageResolution";
import { SubcriterionAssessmentDetails } from "./ScanContentFitnessAssessmentDetails";
import {
  DimensionSummaryPanels,
  DimensionTechnicalDetail,
  ScorePanel,
} from "./ScanContentFitnessParts";
import { AffectedPages, LinkedIssues } from "./ScanContentFitnessSections";
import { CanonicalSubcriteriaSummary } from "./ScanContentFitnessSubcriteria";
import { ScanBreadcrumbs } from "./ScanRouteChrome";
import {
  contentFitnessPageCoverage,
  type OverviewPage,
  type PillarSignal,
} from "./scanModel";

export function DimensionDetail(props: {
  data: OverviewResponse;
  dimension: ContentFitnessDimension;
  signal: PillarSignal | null;
  assessments: DimensionAssessment[];
  assessmentsLoading: boolean;
  websiteId: string;
  origin: ContentFitnessOrigin;
}) {
  const pilotSubcriteria = createMemo(() =>
    contentFitnessPilotSubcriteriaForDimension(props.dimension.key),
  );
  const canonicalCount = createMemo(() =>
    contentFitnessCanonicalCountForDimension(props.dimension.key),
  );
  const canonicalSubcriteria = createMemo(() =>
    CONTENT_FITNESS_SUBCRITERIA.filter(
      (item) => item.parentMetricKey === props.dimension.key,
    ),
  );
  const pagesById = createMemo(
    () => new Map(props.data.pages.map((page) => [page.id, page])),
  );
  const rows = createMemo(() =>
    assessmentRowsForDimension(
      props.assessments,
      pagesById(),
      new Set(pilotSubcriteria().map((item) => item.key)),
    ),
  );
  const assessedPages = createMemo(() =>
    uniquePages(rows().map((row) => row.page)),
  );
  const pageCoverage = createMemo(
    () => props.signal?.coverage ?? contentFitnessPageCoverage(props.data),
  );
  const linkedIssues = createMemo(() =>
    props.data.issues
      .filter(isOpen)
      .filter((issue) => issueBelongsToDimension(issue, props.dimension.key)),
  );
  const linkedIssueCountForPage = (page: OverviewPage) =>
    linkedIssues().filter((issue) =>
      resolveIssuePages(props.data, issue).some(
        (resolution) => resolution.page?.id === page.id,
      ),
    ).length;
  const parent = () =>
    props.origin === CONTENT_FITNESS_ORIGIN.SCORECARD
      ? {
          label: "Scorecard",
          href: `/w/${props.websiteId}/scan/signals`,
        }
      : { label: "Summary", href: `/w/${props.websiteId}/scan` };

  return (
    <>
      <ScanBreadcrumbs
        items={[
          { label: "Signal Scan", href: `/w/${props.websiteId}/scan` },
          parent(),
          { label: props.dimension.label },
        ]}
      />

      <div class="scan-detail-head">
        <div class="scan-detail-title-row">
          <div>
            <span class="scan-kicker">Content Fitness</span>
            <h1 class="scan-title">{props.dimension.label}</h1>
            <p class="scan-lede">{props.dimension.description}</p>
          </div>
          <ScorePanel signal={props.signal} />
        </div>
      </div>

      <DimensionSummaryPanels
        dimension={props.dimension}
        pageCoverage={pageCoverage()}
        signal={props.signal}
        assessments={props.assessments}
        pilotCount={pilotSubcriteria().length}
        canonicalCount={canonicalCount()}
      />

      <CanonicalSubcriteriaSummary
        subcriteria={canonicalSubcriteria()}
        rows={rows()}
        loading={props.assessmentsLoading}
      />

      <div class="scan-grid-two scan-detail-grid">
        <AffectedPages
          assessedPages={assessedPages()}
          loading={props.assessmentsLoading}
          websiteId={props.websiteId}
          site={props.data.site}
          assessments={props.assessments}
          dimensionKey={props.dimension.key}
          linkedIssueCountForPage={linkedIssueCountForPage}
          origin={props.origin}
        />
        <LinkedIssues
          issues={linkedIssues()}
          assessments={props.assessments}
          websiteId={props.websiteId}
          dimensionKey={props.dimension.key}
          origin={props.origin}
        />
      </div>

      <SubcriterionAssessmentDetails
        subcriteria={canonicalSubcriteria()}
        rows={rows()}
        loading={props.assessmentsLoading}
        site={props.data.site}
        websiteId={props.websiteId}
        dimensionKey={props.dimension.key}
        origin={props.origin}
      />

      <DimensionTechnicalDetail
        dimension={props.dimension}
        pilotCount={pilotSubcriteria().length}
        canonicalCount={canonicalCount()}
      />
    </>
  );
}
